// src/services/adminApi.js
import { apiRequest, apiUpload } from './api';

function profileAvatarForm(fields = {}, image = null) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null) formData.append(key, String(value));
    }
    if (image) formData.append('image', image);
    return formData;
}

function challengeLoaderForm(fields = {}, video = null) {
    const formData = new FormData();
    for (const [key, value] of Object.entries(fields)) {
        if (value === undefined || value === null) continue;
        formData.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
    }
    if (video) formData.append('video', video);
    return formData;
}

const adminApi = {
    // Overview
    async getOverviewStats() {
        return await apiRequest('/admin/stats/overview');
    },

    async getOnboardingStats(params = {}) {
        return await apiRequest('/admin/stats/onboarding', { params });
    },

    async getActivationFunnel(params = {}) {
        return await apiRequest('/admin/stats/activation', { params });
    },

    // My Top Movies acquisition funnel (guests included)
    async getTopMoviesFunnel(params = {}) {
        return await apiRequest('/admin/stats/top-movies', { params });
    },

    // Custom Rankings creation + acquisition funnels (guests included)
    async getRankingFunnel(params = {}) {
        return await apiRequest('/admin/stats/rankings', { params });
    },

    // Onboarding diagnostics (same cross-filters as the activation funnel)
    async getOnboardingDiagnostics(params = {}) {
        return await apiRequest('/admin/stats/onboarding-diagnostics', { params });
    },

    async listOnboardingDiagnosticUsers(params = {}) {
        return await apiRequest('/admin/stats/onboarding-diagnostics/users', { params });
    },

    async getUserOnboardingJourney(userId) {
        return await apiRequest(`/admin/users/${userId}/onboarding-journey`);
    },

    // Feedback
    async listFeedback(params = {}) {
        return await apiRequest('/admin/feedback', { params });
    },

    async getFeedback(id) {
        return await apiRequest(`/admin/feedback/${id}`);
    },

    async updateFeedbackStatus(id, status) {
        return await apiRequest(`/admin/feedback/${id}/status`, {
            method: 'PATCH',
            body: { status }
        });
    },

    // Quiz Reports
    async listQuizReports(params = {}) {
        return await apiRequest('/admin/quiz-reports', { params });
    },

    async getQuizReport(id) {
        return await apiRequest(`/admin/quiz-reports/${id}`);
    },

    async updateQuizReportStatus(id, status) {
        return await apiRequest(`/admin/quiz-reports/${id}/status`, {
            method: 'PATCH',
            body: { status }
        });
    },

    // Users
    async listUsers(params = {}) {
        return await apiRequest('/admin/users', { params });
    },

    async getUserDetail(id) {
        return await apiRequest(`/admin/users/${id}`);
    },

    async updateUserRole(id, role) {
        return await apiRequest(`/admin/users/${id}/role`, {
            method: 'PATCH',
            body: { role }
        });
    },

    async setFoundingCollector(id, granted) {
        return await apiRequest(`/admin/users/${id}/founding-collector`, {
            method: 'PATCH',
            body: { granted }
        });
    },

    async sendUserEmail(id, payload) {
        return await apiRequest(`/admin/users/${id}/email`, {
            method: 'POST',
            body: payload
        });
    },

    // Coins
    async listCoins(params = {}) {
        return await apiRequest('/admin/coins', { params });
    },

    async getCoin(id) {
        return await apiRequest(`/admin/coins/${id}`);
    },

    // User Reports
    async listUserReports(params = {}) {
        return await apiRequest('/admin/reports', { params });
    },

    async updateUserReportStatus(id, status) {
        return await apiRequest(`/admin/reports/${id}/status`, {
            method: 'PATCH',
            body: { status },
        });
    },

    // Collections
    async listCollections(params = {}) {
        return await apiRequest('/admin/collections', { params });
    },

    async getCollection(id) {
        return await apiRequest(`/admin/collections/${id}`);
    },

    async listCollectionOwners(id, params = {}) {
        return await apiRequest(`/admin/collections/${id}/owners`, { params });
    },

    async createCollection(payload) {
        return await apiRequest('/admin/collections', {
            method: 'POST',
            body: payload,
        });
    },

    async updateCollection(id, payload) {
        return await apiRequest(`/admin/collections/${id}`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async toggleCollectionActive(id, active) {
        return await apiRequest(`/admin/collections/${id}/active`, {
            method: 'PATCH',
            body: { active },
        });
    },

    async addCollectionItem(id, payload) {
        return await apiRequest(`/admin/collections/${id}/items`, {
            method: 'POST',
            body: payload,
        });
    },

    async removeCollectionItem(id, itemId) {
        return await apiRequest(`/admin/collections/${id}/items/${itemId}`, {
            method: 'DELETE',
        });
    },

    async updateCollectionCoin(id, payload) {
        return await apiRequest(`/admin/collections/${id}/coin`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async removeCollectionCoin(id, variant = 'all') {
        return await apiRequest(`/admin/collections/${id}/coin`, {
            method: 'DELETE',
            body: { variant },
        });
    },

    async generateCollectionExamples(id) {
        return await apiRequest(`/admin/collections/${id}/generate-examples`, {
            method: 'POST',
        });
    },

    // ── Profile avatar library (Admin → Avatars) ─────────────────────────
    async listProfileAvatars() {
        return await apiRequest('/admin/profile-avatars');
    },

    async getProfileAvatar(id) {
        return await apiRequest(`/admin/profile-avatars/${id}`);
    },

    // fields: { name, category_id, alt_text, sort_order, is_active, unlock_type, unlock_kind,
    //          unlock_min, unlock_collection_id, unlock_deadline, unlock_description };
    // image: File (required on create)
    async createProfileAvatar(fields, image) {
        return await apiUpload('/admin/profile-avatars', profileAvatarForm(fields, image));
    },

    // Every field optional; a new image replaces the avatar's image in place.
    async updateProfileAvatar(id, fields, image = null) {
        return await apiUpload(`/admin/profile-avatars/${id}`, profileAvatarForm(fields, image), false, { method: 'PATCH' });
    },

    async deleteProfileAvatar(id) {
        return await apiRequest(`/admin/profile-avatars/${id}`, { method: 'DELETE' });
    },

    // Avatar categories (admin-managed, user-facing)
    async listProfileAvatarCategories() {
        return await apiRequest('/admin/profile-avatar-categories');
    },

    async createProfileAvatarCategory(fields) {
        return await apiRequest('/admin/profile-avatar-categories', { method: 'POST', body: fields });
    },

    async updateProfileAvatarCategory(id, fields) {
        return await apiRequest(`/admin/profile-avatar-categories/${id}`, { method: 'PATCH', body: fields });
    },

    async deleteProfileAvatarCategory(id) {
        return await apiRequest(`/admin/profile-avatar-categories/${id}`, { method: 'DELETE' });
    },

    // ── Challenge loader library (Admin → Challenge Loaders) ──────────────
    async listChallengeLoaders() {
        return await apiRequest('/admin/challenge-loaders');
    },

    async getChallengeLoader(id) {
        return await apiRequest(`/admin/challenge-loaders/${id}`);
    },

    // multipart: video*, name*, description, category_ids, is_general, is_active,
    // weight, sort_order, available_from, available_until, ai_analysis_token
    async createChallengeLoader(fields, video) {
        return await apiUpload('/admin/challenge-loaders', challengeLoaderForm(fields, video));
    },

    // Every field optional; a new video replaces the loader's MP4 in place.
    async updateChallengeLoader(id, fields, video = null) {
        return await apiUpload(`/admin/challenge-loaders/${id}`, challengeLoaderForm(fields, video), false, { method: 'PATCH' });
    },

    async deleteChallengeLoader(id) {
        return await apiRequest(`/admin/challenge-loaders/${id}`, { method: 'DELETE' });
    },

    // Optional AI suggestions — only when the admin clicks the button.
    async analyzeChallengeLoaderVideo(video) {
        return await apiUpload('/admin/challenge-loaders/analyze', challengeLoaderForm({}, video));
    },

    async analyzeSavedChallengeLoader(id) {
        return await apiRequest(`/admin/challenge-loaders/${id}/analyze`, { method: 'POST', retry: { retries: 0, timeoutMs: 60000 } });
    },

    async createChallengeLoaderCategory(fields) {
        return await apiRequest('/admin/challenge-loader-categories', { method: 'POST', body: fields });
    },

    async updateChallengeLoaderCategory(id, fields) {
        return await apiRequest(`/admin/challenge-loader-categories/${id}`, { method: 'PATCH', body: fields });
    },

    async deleteChallengeLoaderCategory(id) {
        return await apiRequest(`/admin/challenge-loader-categories/${id}`, { method: 'DELETE' });
    },

    async uploadCollectionImage(id, file, variant = 'standard') {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('variant', variant);
        return await apiUpload(`/admin/collections/${id}/upload-image`, formData);
    },

    // Coin grants (support tooling): give / remove a user's coins
    async listUserCoins(userId) {
        return await apiRequest(`/admin/users/${userId}/coins`);
    },

    // payload: { type: 'collection', collection_id, earned_month? }
    //        | { type: 'media', tmdb_id, kind, season_number?, rarity, earned_month? }
    async grantUserCoin(userId, payload) {
        return await apiRequest(`/admin/users/${userId}/coins`, { method: 'POST', body: payload });
    },

    async revokeUserCoin(userId, coinId) {
        return await apiRequest(`/admin/users/${userId}/coins/${coinId}`, { method: 'DELETE' });
    },

    // Animated ("Signature") reward coin media
    async getRewardMedia(id) {
        return await apiRequest(`/admin/collections/${id}/reward-media`);
    },

    async uploadRewardAnimation(id, file) {
        const formData = new FormData();
        formData.append('animation', file);
        return await apiUpload(`/admin/collections/${id}/reward-media`, formData);
    },

    async setRewardMediaType(id, mediaType) {
        return await apiRequest(`/admin/collections/${id}/reward-media-type`, {
            method: 'PATCH',
            body: { media_type: mediaType },
        });
    },

    async retryRewardMedia(id, mediaId) {
        return await apiRequest(`/admin/collections/${id}/reward-media/${mediaId}/retry`, { method: 'POST' });
    },

    async retryRewardGif(id, mediaId) {
        return await apiRequest(`/admin/collections/${id}/reward-media/${mediaId}/retry-gif`, { method: 'POST' });
    },

    async uploadRewardPoster(id, mediaId, file) {
        const formData = new FormData();
        formData.append('image', file);
        return await apiUpload(`/admin/collections/${id}/reward-media/${mediaId}/poster`, formData);
    },

    async resetRewardPoster(id, mediaId) {
        return await apiRequest(`/admin/collections/${id}/reward-media/${mediaId}/poster`, { method: 'DELETE' });
    },

    // Curated Lists
    async listCuratedLists(params = {}) {
        return await apiRequest('/admin/curated-lists', { params });
    },

    async getCuratedList(id) {
        return await apiRequest(`/admin/curated-lists/${id}`);
    },

    async createCuratedList(payload) {
        return await apiRequest('/admin/curated-lists', {
            method: 'POST',
            body: payload,
        });
    },

    async updateCuratedList(id, payload) {
        return await apiRequest(`/admin/curated-lists/${id}`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async uploadCuratedListCover(id, file) {
        const formData = new FormData();
        formData.append('image', file);
        return await apiUpload(`/admin/curated-lists/${id}/cover-image`, formData);
    },

    async archiveCuratedList(id) {
        return await apiRequest(`/admin/curated-lists/${id}`, {
            method: 'DELETE',
        });
    },

    async addCuratedListItem(id, payload) {
        return await apiRequest(`/admin/curated-lists/${id}/items`, {
            method: 'POST',
            body: payload,
        });
    },

    async updateCuratedListItem(id, itemId, payload) {
        return await apiRequest(`/admin/curated-lists/${id}/items/${itemId}`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async removeCuratedListItem(id, itemId) {
        return await apiRequest(`/admin/curated-lists/${id}/items/${itemId}`, {
            method: 'DELETE',
        });
    },

    async reorderCuratedListItems(id, orderedItemIds) {
        return await apiRequest(`/admin/curated-lists/${id}/items/reorder`, {
            method: 'PATCH',
            body: { orderedItemIds },
        });
    },

    // Home feed: Aluvo Picks placements (docs/home-feed.md)
    async listEditorialPicks() {
        return await apiRequest('/admin/editorial-picks');
    },

    // payload: { itemType, reference, note?, priority?, isActive? }
    async createEditorialPick(payload) {
        return await apiRequest('/admin/editorial-picks', {
            method: 'POST',
            body: payload,
        });
    },

    // payload: { note?, priority?, isActive? }
    async updateEditorialPick(id, payload) {
        return await apiRequest(`/admin/editorial-picks/${id}`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async deleteEditorialPick(id) {
        return await apiRequest(`/admin/editorial-picks/${id}`, {
            method: 'DELETE',
        });
    },

    // Quiz Questions
    async listQuizQuestions(params = {}) {
        return await apiRequest('/admin/quiz-questions', { params });
    },

    async getQuizQuestion(id) {
        return await apiRequest(`/admin/quiz-questions/${id}`);
    },

    async createQuizQuestion(payload) {
        return await apiRequest('/admin/quiz-questions', {
            method: 'POST',
            body: payload,
        });
    },

    async updateQuizQuestion(id, payload) {
        return await apiRequest(`/admin/quiz-questions/${id}`, {
            method: 'PATCH',
            body: payload,
        });
    },

    async toggleQuizQuestionActive(id, active) {
        return await apiRequest(`/admin/quiz-questions/${id}/active`, {
            method: 'PATCH',
            body: { active },
        });
    },

    // Quiz pools: private drafts, review and (early) publication — docs/quiz-pools.md
    async searchQuizPoolTitles(query, kind) {
        return await apiRequest('/admin/quiz-pools/search', { params: { query, kind } });
    },

    async listQuizPoolSeasons(tmdbId) {
        return await apiRequest('/admin/quiz-pools/seasons', { params: { tmdbId } });
    },

    // target: { tmdbId, kind, seasonNumber? }
    async getQuizPool(target) {
        return await apiRequest('/admin/quiz-pools', { params: target });
    },

    async saveQuizPoolDraft(target, { questions, notes, baseRevision }) {
        return await apiRequest('/admin/quiz-pools/draft', {
            method: 'PUT',
            body: { ...target, questions, notes, baseRevision },
        });
    },

    async generateQuizPoolDraft(target, baseRevision) {
        return await apiRequest('/admin/quiz-pools/draft/generate', {
            method: 'POST',
            body: { ...target, baseRevision },
        });
    },

    async publishQuizPool(target, baseRevision) {
        return await apiRequest('/admin/quiz-pools/publish', {
            method: 'POST',
            body: { ...target, baseRevision, confirm: true },
        });
    },

    async unpublishQuizPool(target) {
        return await apiRequest('/admin/quiz-pools/unpublish', {
            method: 'POST',
            body: { ...target, confirm: true },
        });
    },

    async withdrawQuizPoolQuestion(questionId, reason) {
        return await apiRequest(`/admin/quiz-pools/questions/${questionId}/withdraw`, {
            method: 'POST',
            body: { reason },
        });
    },

    // Email campaigns (optional product / marketing email)
    async getCampaignConfig() {
        return await apiRequest('/admin/campaigns/config');
    },

    async listCampaigns(params = {}) {
        return await apiRequest('/admin/campaigns', { params });
    },

    async getCampaign(id) {
        return await apiRequest(`/admin/campaigns/${id}`);
    },

    // Notification emails (Admin → Emails)
    async getEmailNotificationSummary(params = {}) {
        return await apiRequest('/admin/email-notifications/summary', { params });
    },

    async listEmailNotifications(params = {}) {
        return await apiRequest('/admin/email-notifications', { params });
    },

    async listCampaignUnsubscribes(id, { page = 1 } = {}) {
        return await apiRequest(`/admin/campaigns/${id}/unsubscribes`, { params: { page } });
    },

    async createCampaign(payload) {
        return await apiRequest('/admin/campaigns', { method: 'POST', body: payload });
    },

    async updateCampaign(id, payload) {
        return await apiRequest(`/admin/campaigns/${id}`, { method: 'PATCH', body: payload });
    },

    async duplicateCampaign(id) {
        return await apiRequest(`/admin/campaigns/${id}/duplicate`, { method: 'POST' });
    },

    async estimateCampaignAudience(audience) {
        return await apiRequest('/admin/campaigns/estimate', { method: 'POST', body: { audience } });
    },

    async previewCampaign(id, overrides = {}) {
        const path = id ? `/admin/campaigns/${id}/preview` : '/admin/campaigns/preview';
        return await apiRequest(path, { method: 'POST', body: overrides });
    },

    /** Pre-send validation report (same rules schedule / send enforce). Overrides = unsaved edits. */
    async validateCampaign(id, overrides = {}) {
        const path = id ? `/admin/campaigns/${id}/validate` : '/admin/campaigns/validate';
        return await apiRequest(path, { method: 'POST', body: overrides });
    },

    async sendCampaignTest(id) {
        return await apiRequest(`/admin/campaigns/${id}/test`, { method: 'POST', retry: false });
    },

    async scheduleCampaign(id, { scheduledAt, timezone, acknowledgedWarnings = [] }) {
        return await apiRequest(`/admin/campaigns/${id}/schedule`, { method: 'POST', body: { scheduledAt, timezone, acknowledgedWarnings } });
    },

    async unscheduleCampaign(id) {
        return await apiRequest(`/admin/campaigns/${id}/unschedule`, { method: 'POST' });
    },

    async cancelCampaign(id) {
        return await apiRequest(`/admin/campaigns/${id}/cancel`, { method: 'POST' });
    },

    async sendCampaignNow(id, { acknowledgedWarnings = [] } = {}) {
        return await apiRequest(`/admin/campaigns/${id}/send`, { method: 'POST', body: { confirm: true, acknowledgedWarnings }, retry: false });
    },

    async uploadCampaignHeroImage(id, file, alt) {
        const formData = new FormData();
        formData.append('image', file);
        if (alt) formData.append('alt', alt);
        return await apiUpload(`/admin/campaigns/${id}/hero-image`, formData);
    },

    // Media (typeahead)
    async searchMedia(params = {}) {
        return await apiRequest('/admin/media', { params });
    },

    async searchProviderMedia(params = {}) {
        return await apiRequest('/media/search', { params });
    },
};

export default adminApi;
